import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Maximize2 } from 'lucide-react';
import { Button } from '@nyce/admin-kit/ui';
import { ErrorBox, useAsync, useInterval } from '../../components/common';
import { stats } from '../../api/stats';
import { formatRelative } from '../../lib/format';
import { Kpi } from '../dashboard/shared';
import { silentSince, useStatsSummary } from '../dashboard/stats';
import { IngestStatus } from './shared';
import './statistics.css';

const REFRESH_MS = 60_000;

/**
 * Office TV screen (decision 1, replaces PMS's BigScreen and its 10-year URL token): fleet tiles, silent installations,
 * open incidents and regressions, refreshed every minute. Rendered without the sidebar, behind the normal sign-in; the
 * session refreshes itself, so the screen stays signed in while the browser stays open.
 */
export function StatisticsScreenPage() {
  const summary = useStatsSummary(true);
  const incidents = useAsync(() => stats.incidents({ status: 'open', limit: 8 }), []);
  const [now, setNow] = useState(() => Date.now());
  useInterval(
    () => {
      summary.reload();
      void incidents.reload();
      setNow(Date.now());
    },
    REFRESH_MS,
    true
  );
  useEffect(() => {
    document.title = 'Statistics · Operations Hub';
  }, []);
  const s = summary.data;
  const f = s?.fleet;
  return (
    <div className="sx-kiosk" data-testid="kiosk">
      <div className="sx-kiosk-head">
        <h1>nyce.logic fleet</h1>
        <span className="nyk-ui-hint">updated {new Date(now).toLocaleTimeString()} · every minute</span>
        <span className="sx-spacer" />
        <Button
          size="sm"
          variant="ghost"
          icon={<Maximize2 size={14} />}
          onClick={() => void document.documentElement.requestFullscreen?.().catch(() => undefined)}
        >
          Full screen
        </Button>
        <Link to="/statistics" className="nyk-link">
          Exit
        </Link>
      </div>
      <ErrorBox error={summary.error} />
      {f && s && (
        <>
          <div className="nyk-kpis">
            <Kpi
              label="Installations reporting"
              value={`${f.installations.ok} / ${f.installations.ok + f.installations.silent}`}
              tone={f.installations.silent ? 'red' : 'green'}
            />
            <Kpi
              label="Silent"
              value={f.installations.silent}
              tone={f.installations.silent ? 'red' : undefined}
            />
            <Kpi
              label="Open incidents"
              value={f.incidentsOpen}
              tone={f.incidentsOpen ? 'amber' : undefined}
            />
            <Kpi label="New error groups 24 h" value={f.errorGroupsNew24h} />
            <Kpi
              label="Ingest"
              value={s.ingest?.processor.stale ? 'stale' : 'ok'}
              tone={s.ingest?.processor.stale ? 'red' : 'green'}
            />
          </div>
          <div className="sx-kiosk-cols">
            <div className="nyk-card">
              <div className="nyk-card-head">
                <h2>Silent installations</h2>
              </div>
              {!s.silent.length ? (
                <p>All installations report.</p>
              ) : (
                <ul className="sx-kiosk-list">
                  {s.silent.slice(0, 10).map((i) => (
                    <li key={`${i.customerId}:${i.workserver}`}>
                      <IngestStatus status={i.status} />
                      <span>
                        {i.name ?? i.customerId} · {i.workserver}
                      </span>
                      <span className="sx-spacer" />
                      <span className="nyk-ui-hint">{formatRelative(silentSince(i), now)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="nyk-card">
              <div className="nyk-card-head">
                <h2>Open incidents, last 30 days</h2>
              </div>
              {!incidents.data?.items.length ? (
                <p>No open incidents.</p>
              ) : (
                <ul className="sx-kiosk-list">
                  {incidents.data.items.map((i) => (
                    <li key={i.id}>
                      <span>{i.name ?? i.customerId}</span>
                      <span className="nyk-ui-hint">{i.methods.slice(0, 2).join(', ')}</span>
                      <span className="sx-spacer" />
                      <span className="nyk-ui-hint">{formatRelative(i.startedAt, now)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="nyk-card">
              <div className="nyk-card-head">
                <h2>Slower than usual (24 h)</h2>
              </div>
              {!s.regressions.length ? (
                <p>No regressions.</p>
              ) : (
                <ul className="sx-kiosk-list">
                  {s.regressions.slice(0, 8).map((r) => (
                    <li key={`${r.customerId}:${r.workserver}:${r.method}`}>
                      <span>
                        {r.name ?? r.customerId} · {r.workserver}
                      </span>
                      <span className="nyk-ui-hint">{r.method}</span>
                      <span className="sx-spacer" />
                      <b>×{r.ratio.toFixed(1)}</b>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="nyk-card">
              <div className="nyk-card-head">
                <h2>WMS versions</h2>
              </div>
              <ul className="sx-kiosk-list">
                {f.versions.slice(0, 8).map((v) => (
                  <li key={v.version}>
                    <span>{v.version}</span>
                    <span className="sx-spacer" />
                    <b>{v.installations}</b>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
